export default class Pattern {
  constructor (raw) {
    raw = escapeRegExp(raw)
    this.nodes = Pattern.SYNTACES.map(syntax => syntax.extract(raw)).reduce((arr, subarr) => arr.concat(subarr), [])

    const regexText = this.nodes.reduce((regex, node) => regex.replace(node.raw, node.regex), raw)
    this.regex = new RegExp(`^${regexText}$`)
  }

  match (raw) {
    return keyOnly(raw).match(this.regex)
  }

  extract (raw) {
    const match = this.match(raw)
    return this.nodes.map(node => node.evaluate(raw, match))
  }
}

Pattern.Syntax = class PatternSyntax { }

Pattern.Param = class PatternParam extends Pattern.Syntax {
  constructor (raw, { name, type, defaultValue }) {
    super()
    Object.assign(this, { raw, name, type, defaultValue })
    this.regex = `\\s*\\(?(?<${this._key()}>${Pattern.ANY})${defaultValue ? '?' : ''}\\)?\\s*`
  }

  evaluate (raw, match) {
    return {
      name: this.name,
      type: this.type,
      value: match.groups[this._key()]?.trim() || this.defaultValue
    }
  }

  _key () {
    return this.name.split(' ').join('_')
  }

  static extract (raw) {
    const match = raw.matchAll(Pattern.Param.EXTRACTION_REGEX)
    if (!match) return []
    return Array.from(match)
      .map(match => {
        const { input, groups } = match[0].match(/<((?<type>\w*):)?(?<name>\w*)(=(?<defaultValue>\w*))?>/)
        const { name, type, defaultValue } = groups
        return new Pattern.Param(input, { name, type: type || name, defaultValue })
      })
  }
}
Pattern.Param.EXTRACTION_REGEX = /<(\w*:?\w*=?\w*)>/g

Pattern.RawParam = class PatternRawParam extends Pattern.Syntax {
  constructor (raw, { name, type, defaultValue }) {
    super()
    Object.assign(this, { raw, name, type, defaultValue })
    this.regex = `\\s*\\(?(?<${this._key()}>${Pattern.ANY})${defaultValue ? '?' : ''}\\)?\\s*`
  }

  evaluate (raw, match) {
    return {
      raw: true,
      name: this.name,
      type: this.type,
      value: match.groups[this._key()]?.trim() || this.defaultValue
    }
  }

  _key () {
    return this.name.split(' ').join('_')
  }

  static extract (raw) {
    const match = raw.matchAll(Pattern.RawParam.EXTRACTION_REGEX)
    if (!match) return []
    return Array.from(match)
      .map(match => {
        const { input, groups } = match[0].match(/<%((?<type>\w*):)?(?<name>\w*)(=(?<defaultValue>\w*))?%>/)
        const { name, type, defaultValue } = groups
        return new Pattern.RawParam(input, { name, type: type || name, defaultValue })
      })
  }
}
Pattern.RawParam.EXTRACTION_REGEX = /<%(\w*:?\w*=?\w*)%>/g

Pattern.Flag = class PatternFlag extends Pattern.Syntax {
  constructor ({ name }) {
    super()
    Object.assign(this, { name })
    this.raw = `[${name}]`
    this.regex = `(?<${this._key()}>${name})?`
  }

  evaluate (raw, match) {
    return {
      name: this.name,
      type: 'boolean',
      value: Boolean(match.groups[this._key()])
    }
  }

  _key () {
    return this.name.split(' ').join('_')
  }

  static extract (raw) {
    const match = raw.matchAll(Pattern.Flag.EXTRACTION_REGEX)
    if (!match) return []
    return Array.from(match).map(match => match[1]).map(name => new Pattern.Flag({ name }))
  }
}
Pattern.Flag.EXTRACTION_REGEX = /\[(.+?)\]/g

Pattern.Options = class PatternOptions extends Pattern.Syntax {
  constructor (name) {
    super()
    Object.assign(this, { name })
    this.raw = new RegExp(`\\s*{${name}}\\s*`)
    this.regex = ''
  }

  evaluate (raw) {
    return {
      name: this.name,
      type: 'options',
      value: valueOnly(raw)
    }
  }

  static extract (raw) {
    const match = raw.match(Pattern.Options.REGEX)
    return match ? [new Pattern.Options(match[1])] : []
  }
}
Pattern.Options.REGEX = /{(.+?)}/

Pattern.SYNTACES = [Pattern.RawParam, Pattern.Param, Pattern.Flag, Pattern.Options]
Pattern.ANY = (() => {
  const MAX_DEPTH = 2
  const BASIC = '[^()]+?'
  return Array(MAX_DEPTH).fill(1).reduce(prev => `(${prev}|(\\(${prev}\\)${prev})+?|(${prev}\\(${prev}\\))+?|(${prev}\\(${prev}\\)${prev})+?)`, BASIC)
}) ()

const keyOnly = raw => raw.constructor === String ? raw : Object.keys(raw)[0]
const valueOnly = raw => raw.constructor !== String ? Object.values(raw)[0] : {}
const escapeRegExp = string => string.replace(/[-\/\\^$*+?.()|[\]]/g, '\\$&')
